import { useState, useEffect, useCallback } from 'react';

interface CountdownTimerProps {
  deadline: string; // ISO date string
  className?: string;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
}

function calculateTimeLeft(deadline: string): TimeLeft {
  const total = new Date(deadline).getTime() - Date.now();

  if (total <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
  }

  return {
    days: Math.floor(total / (1000 * 60 * 60 * 24)),
    hours: Math.floor((total % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((total % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((total % (1000 * 60)) / 1000),
    total,
  };
}

function formatTimeUnit(value: number): string {
  return value.toString().padStart(2, '0');
}

export default function CountdownTimer({ deadline, className = '' }: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() => calculateTimeLeft(deadline));
  const [isExpired, setIsExpired] = useState(false);

  const updateTimer = useCallback(() => {
    const newTimeLeft = calculateTimeLeft(deadline);
    setTimeLeft(newTimeLeft);
    setIsExpired(newTimeLeft.total <= 0);
  }, [deadline]);

  useEffect(() => {
    updateTimer();

    const timer = setInterval(updateTimer, 1000);

    return () => clearInterval(timer);
  }, [updateTimer]);

  if (isExpired) {
    return (
      <div className={`inline-flex items-center gap-1 text-red-400 font-medium ${className}`}>
        <span>Expired</span>
      </div>
    );
  }

  // Polymarket style: show days if > 0, otherwise show hours:mins:secs
  const showDays = timeLeft.days > 0;
  const showHours = !showDays || timeLeft.hours > 0;

  return (
    <div className={`inline-flex items-center gap-1 font-mono text-sm ${className}`}>
      {showDays && (
        <>
          <span className="bg-dark-700 text-green-400 px-2 py-1 rounded font-bold min-w-[2rem] text-center">
            {timeLeft.days}
          </span>
          <span className="text-gray-500 text-xs">d</span>
        </>
      )}

      {showHours && (
        <>
          <span className="bg-dark-700 text-green-400 px-2 py-1 rounded font-bold min-w-[2rem] text-center">
            {formatTimeUnit(timeLeft.hours)}
          </span>
          <span className="text-gray-500 text-xs">h</span>
        </>
      )}

      <span className="bg-dark-700 text-green-400 px-2 py-1 rounded font-bold min-w-[2rem] text-center">
        {formatTimeUnit(timeLeft.minutes)}
      </span>
      <span className="text-gray-500 text-xs">m</span>

      {!showDays && (
        <>
          <span className="bg-dark-700 text-green-400 px-2 py-1 rounded font-bold min-w-[2rem] text-center">
            {formatTimeUnit(timeLeft.seconds)}
          </span>
          <span className="text-gray-500 text-xs">s</span>
        </>
      )}
    </div>
  );
}
